/**
 * Formats GMV (Gross Merchandise Value) for display
 * @param {number} gmv - The GMV value to format
 * @returns {string} Formatted GMV string
 */
export const formatGMV = gmv => {
  if (!gmv || gmv === 0) {
    return 'No GMV Data';
  }

  const withCommas = (value, fractionDigits) =>
    value.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

  if (gmv >= 1000000) {
    return `$${withCommas(gmv / 1000000, 1)}M`;
  } else if (gmv >= 1000) {
    return `$${withCommas(gmv / 1000, 1)}K`;
  } else {
    return `$${withCommas(gmv, 0)}`;
  }
};

/**
 * Formats currency values for display
 * @param {number} value - The currency value to format
 * @param {string} currency - Currency symbol (default: '$')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = '$') => {
  if (!value || value === 0) {
    return `${currency}0`;
  }

  return `${currency}${value.toLocaleString()}`;
};

/**
 * Formats a numeric value as a rounded whole-dollar currency string.
 * Returns null when the value cannot be parsed (e.g. empty cell rendering).
 * @param {number|string} value - The value to format
 * @returns {string|null} e.g. "$1,234,567" or null
 */
export const formatRoundedCurrency = value => {
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  return '$' + Math.round(num).toLocaleString('en-US');
};

export const formatCurrencyWithCommas = value => {
  if (value === null || value === undefined || value === '') {
    return '$0';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return '$0';
  }

  return `$${numValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};
